/**
 * Chat state: an ordered list of user and assistant messages plus the conversation id.
 *
 * Assistant message:
 *   {id, role:'assistant', messageId, text, status:'pending'|'streaming'|'complete'|'stopped'|'error',
 *    stage, stageLabel, stageDetail, sources, sourceById, citedIds, grounded, figuresRequested,
 *    followups, followupsPrompt, notice, mode, timing, protocol, finishReason, error, question, busy}
 * `status` describes the answer text; `busy` stays true until `done`/`error`/stop, so follow-ups can
 * still arrive after the answer is complete.
 */

export const ACTIONS = Object.freeze({
  SEND: 'SEND',
  META: 'META',
  STATUS: 'STATUS',
  SOURCES: 'SOURCES',
  DELTA_BATCH: 'DELTA_BATCH',
  ANSWER_END: 'ANSWER_END',
  FOLLOWUPS: 'FOLLOWUPS',
  DONE: 'DONE',
  ERROR: 'ERROR',
  STOP: 'STOP',
  RETRY: 'RETRY',
  LOAD_HISTORY: 'LOAD_HISTORY',
  PREPEND_HISTORY: 'PREPEND_HISTORY',
  RESET: 'RESET',
});

export const initialChatState = Object.freeze({
  messages: [],
  conversationId: '',
  activeId: null,
  // long-chat indicator from meta.conversation / the history detail: {turns, approx_tokens, long, limit_hint}
  conversationInfo: null,
});

export function indexSources(sources) {
  const byId = {};
  (sources || []).forEach((s) => {
    if (s && s.id != null) byId[s.id] = s;
  });
  return byId;
}

export function createAssistantMessage(fields = {}) {
  const sources = fields.sources || [];
  return {
    id: fields.id,
    role: 'assistant',
    messageId: fields.messageId || '',
    text: fields.text || '',
    status: fields.status || 'pending',
    stage: fields.stage || null,
    stageLabel: fields.stageLabel || '',
    stageDetail: fields.stageDetail || null,
    sources,
    sourceById: fields.sourceById || indexSources(sources),
    sourcesReceived: fields.sourcesReceived != null ? fields.sourcesReceived : sources.length > 0,
    citedIds: fields.citedIds || [],
    grounded: fields.grounded != null ? fields.grounded : null,
    figuresRequested: Boolean(fields.figuresRequested),
    followups: fields.followups || [],
    followupsPrompt: fields.followupsPrompt || null,
    notice: fields.notice || null,
    mode: fields.mode || null,
    timing: fields.timing || null,
    protocol: fields.protocol || 2,
    finishReason: fields.finishReason || null,
    error: fields.error || null,
    question: fields.question || '',
    busy: Boolean(fields.busy),
    createdAt: fields.createdAt || null,
  };
}

function updateMessage(state, id, update) {
  let changed = false;
  const messages = state.messages.map((m) => {
    if (m.id !== id) return m;
    changed = true;
    return { ...m, ...(typeof update === 'function' ? update(m) : update) };
  });
  return changed ? { ...state, messages } : state;
}

function finish(state, id, update) {
  const next = updateMessage(state, id, (m) => ({ ...update(m), busy: false }));
  return state.activeId === id ? { ...next, activeId: null } : next;
}

export function chatReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SEND: {
      const { userId, assistantId, question, mode } = action;
      return {
        ...state,
        activeId: assistantId,
        messages: [
          ...state.messages,
          { id: userId, role: 'user', text: question },
          createAssistantMessage({ id: assistantId, question, mode, busy: true }),
        ],
      };
    }

    case ACTIONS.RETRY: {
      // Reset an errored/stopped assistant message in place and stream into it again.
      const { id } = action;
      const next = updateMessage(state, id, (m) => createAssistantMessage({
        id: m.id, question: m.question, mode: m.mode, busy: true,
      }));
      return { ...next, activeId: id };
    }

    case ACTIONS.META: {
      const e = action.event || {};
      const next = updateMessage(state, action.id, (m) => ({
        messageId: e.message_id || m.messageId,
        mode: e.mode || m.mode,
        protocol: e.protocol || m.protocol,
        createdAt: e.created_at || m.createdAt,
      }));
      const withInfo = e.conversation && typeof e.conversation === 'object'
        ? { ...next, conversationInfo: e.conversation } : next;
      // the vehicle the turn ran with (the backend may infer it when none was selected); model as the app labels it
      const withVehicle = e.model
        ? { ...withInfo, vehicle: { model: e.model === 'SuperGreat' ? 'Super Great' : e.model, year: e.year || '' } }
        : withInfo;
      return e.conversation_id ? { ...withVehicle, conversationId: e.conversation_id } : withVehicle;
    }

    case ACTIONS.STATUS: {
      const e = action.event || {};
      return updateMessage(state, action.id, {
        stage: e.stage || null,
        stageLabel: e.label || '',
        stageDetail: e.detail || null,
      });
    }

    case ACTIONS.SOURCES: {
      const e = action.event || {};
      const sources = Array.isArray(e.sources) ? e.sources : [];
      return updateMessage(state, action.id, {
        sources,
        sourceById: indexSources(sources),
        sourcesReceived: true,
        figuresRequested: Boolean(e.figures_requested),
      });
    }

    case ACTIONS.DELTA_BATCH: {
      if (!action.text) return state;
      return updateMessage(state, action.id, (m) => (
        m.status === 'complete' || m.status === 'stopped' || m.status === 'error'
          ? {}
          : { text: m.text + action.text, status: 'streaming' }
      ));
    }

    case ACTIONS.ANSWER_END: {
      const e = action.event || {};
      return updateMessage(state, action.id, (m) => {
        const citedIds = Array.isArray(e.cited_ids) ? e.cited_ids : [];
        return {
          text: typeof e.text === 'string' ? e.text : m.text,
          citedIds,
          grounded: e.grounded != null ? Boolean(e.grounded) : citedIds.length > 0,
          finishReason: e.finish_reason || 'stop',
          notice: e.notice || null,
          status: e.finish_reason === 'stopped' ? 'stopped' : 'complete',
        };
      });
    }

    case ACTIONS.FOLLOWUPS: {
      const e = action.event || {};
      const items = Array.isArray(e.items) ? e.items : [];
      return updateMessage(state, action.id, {
        followups: items,
        followupsPrompt: e.prompt || (items.find((i) => i && i.prompt) || {}).prompt || null,
      });
    }

    case ACTIONS.DONE: {
      const e = action.event || {};
      const next = finish(state, action.id, (m) => ({
        messageId: e.message_id || m.messageId,
        timing: e.timing_ms || null,
        status: m.status === 'pending' || m.status === 'streaming' ? 'complete' : m.status,
        stage: null,
        stageLabel: '',
      }));
      return e.conversation_id ? { ...next, conversationId: e.conversation_id } : next;
    }

    case ACTIONS.ERROR: {
      const e = action.event || {};
      return finish(state, action.id, (m) => ({
        status: m.status === 'complete' ? 'complete' : 'error',
        error: {
          code: e.code || 'internal',
          message: e.message || '',
          retryable: e.retryable !== false,
          partial: Boolean(e.partial || m.text),
        },
        stage: null,
        stageLabel: '',
      }));
    }

    case ACTIONS.STOP: {
      return finish(state, action.id, (m) => ({
        status: m.status === 'complete' ? 'complete' : 'stopped',
        finishReason: m.status === 'complete' ? m.finishReason : 'stopped',
        stage: null,
        stageLabel: '',
      }));
    }

    case ACTIONS.LOAD_HISTORY:
      return {
        messages: action.messages || [],
        conversationId: action.conversationId || '',
        activeId: null,
        conversationInfo: action.conversationInfo || null,
      };

    case ACTIONS.PREPEND_HISTORY: {
      const existing = new Set(state.messages.map((m) => m.id));
      const older = (action.messages || []).filter((m) => !existing.has(m.id));
      return older.length ? { ...state, messages: [...older, ...state.messages] } : state;
    }

    case ACTIONS.RESET:
      return { ...initialChatState };

    default:
      return state;
  }
}
